// La colonne latérale : publicité factice, sauvegarde, mode développeur, avertissement.
import { useState } from 'preact/hooks';
import { t } from '../engine/i18n';
import {
  clearSave,
  exportSave,
  forgetRescuedSave,
  importSave,
  rescuedSave,
  retryRescuedSave,
  saveGame,
} from '../engine/save';
import { createInitialState, type GameState } from '../engine/state';
import { getState, setState, update } from '../engine/store';
import {
  DEV_SPEEDS,
  devAddMoney,
  devAddReserve,
  devAdvanceYear,
  devFillEnergy,
  devFirstJob,
  devSpend,
  devTriggerEvent,
  setDevSpeed,
} from '../game/dev';
import { sendBugReport } from '../engine/signalement';
import { GAME_VERSION } from '../engine/version';
import { ageOnDay } from '../engine/time';
import { Card } from './components';
import { STAGE_LABEL } from './labels';
import { DEV_MODE } from './hooks';

/** Le formulaire de signalement : du texte, le contexte de jeu, et rien d'autre — pas d'adresse mail. */
function BugReport({ state }: { state: GameState }) {
  const [open, setOpen] = useState(false);
  const [texte, setTexte] = useState('');
  const [status, setStatus] = useState<'repos' | 'envoi' | 'merci' | 'echec'>('repos');

  const send = async () => {
    setStatus('envoi');
    const ok = await sendBugReport(texte.trim(), {
      version: GAME_VERSION,
      partie: `${t(STAGE_LABEL[state.stage])}, jour ${Math.floor(state.day)}, ${ageOnDay(state.birthDate, state.startDate, state.day)} ans`,
      navigateur: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    });
    setStatus(ok ? 'merci' : 'echec');
    if (ok) setTexte('');
  };

  if (!open) {
    return (
      <button class="bug-link" onClick={() => { setOpen(true); setStatus('repos'); }}>
        {t('side.bug')}
      </button>
    );
  }
  return (
    <div class="bug-form">
      <label class="field">
        <span class="muted">{t('bug.intro')}</span>
        <textarea
          rows={4}
          maxLength={4000}
          value={texte}
          onInput={(event) => setTexte((event.target as HTMLTextAreaElement).value)}
        />
      </label>
      <p class="hint">{t('bug.context', { version: GAME_VERSION })}</p>
      <div class="button-row">
        <button class="button-small" disabled={texte.trim() === '' || status === 'envoi'} onClick={() => void send()}>
          {t(status === 'envoi' ? 'bug.sending' : 'bug.send')}
        </button>
        <button class="button-small" onClick={() => setOpen(false)}>
          {t('bug.close')}
        </button>
      </div>
      {status === 'merci' && <p class="hint">{t('bug.sent')}</p>}
      {status === 'echec' && (
        <p class="warning">
          {t('bug.failed')} <a href={bugMailto(state)}>contact@lewel-labs.fr</a>
        </p>
      )}
    </div>
  );
}

/** Un brouillon de mail pré-rempli, en secours quand le service ne répond pas. */
function bugMailto(state: GameState): string {
  const navigateur = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const body = [
    'Bonjour,',
    '',
    "Décris ici le problème : ce que tu faisais, ce que tu attendais, ce qui s'est passé.",
    '',
    '',
    '— Infos à garder pour nous aider —',
    `Version : ${GAME_VERSION}`,
    `Partie : ${t(STAGE_LABEL[state.stage])}, jour ${Math.floor(state.day)}, ${ageOnDay(state.birthDate, state.startDate, state.day)} ans`,
    `Navigateur : ${navigateur}`,
    "Astuce : le bouton « Exporter » de la carte Sauvegarde copie ta partie ; colle le texte ici si le souci s'y rattache.",
  ].join('\n');
  const subject = `Le Carnet de Suzanne ${GAME_VERSION} — signalement`;
  return `mailto:contact@lewel-labs.fr?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export function Side({ state }: { state: GameState }) {
  return (
    <aside class="side">
      <div class="ad-slot" aria-label={t('ad.label')}>
        <span class="ad-label">{t('ad.label')}</span>
        <p>{t('ad.placeholder')}</p>
        <p class="muted">{t('ad.note')}</p>
      </div>
      <SaveBox />
      {DEV_MODE && <DevPanel state={state} />}
      <p class="disclaimer">{t('footer.disclaimer')}</p>
      <BugReport state={state} />
    </aside>
  );
}

function SaveBox() {
  const [mode, setMode] = useState<'none' | 'export' | 'import' | 'rescue'>('none');
  const [text, setText] = useState('');
  const [error, setError] = useState(false);
  const [rescueStuck, setRescueStuck] = useState(false);
  const rescue = rescuedSave();

  // La sauvegarde mise de côté se charge de nouveau (le jeu a été mis à jour, par exemple).
  const restoreRescue = () => {
    const restored = retryRescuedSave();
    if (!restored) {
      setRescueStuck(true);
      return;
    }
    restored.lastRealTime = Date.now();
    setState(restored);
    saveGame(restored);
    setMode('none');
    setRescueStuck(false);
  };

  const load = () => {
    try {
      const imported = importSave(text);
      imported.lastRealTime = Date.now();
      setState(imported);
      saveGame(imported);
      setMode('none');
      setError(false);
    } catch {
      setError(true);
    }
  };

  const reset = () => {
    if (!window.confirm(t('save.resetConfirm'))) return;
    clearSave();
    const fresh = createInitialState(Date.now());
    setState(fresh);
    saveGame(fresh);
  };

  return (
    <Card title={t('save.title')}>
      <p class="muted">{t('save.auto')}</p>
      <div class="button-row">
        <button class="button-small" onClick={() => { setText(exportSave(getState())); setMode('export'); }}>
          {t('save.export')}
        </button>
        <button class="button-small" onClick={() => { setText(''); setMode('import'); }}>
          {t('save.import')}
        </button>
        <button class="button-small" onClick={reset}>
          {t('save.reset')}
        </button>
      </div>
      {mode === 'export' && (
        <label class="field">
          <span>{t('save.exported')}</span>
          <textarea readOnly rows={4} value={text} onFocus={(event) => (event.target as HTMLTextAreaElement).select()} />
        </label>
      )}
      {rescue !== null && (
        <div class="offer">
          <p class="hint">{t('save.rescued')}</p>
          <div class="button-row">
            <button class="button-small" onClick={restoreRescue}>
              {t('save.rescueRetry')}
            </button>
            <button class="button-small" onClick={() => { setText(rescue); setMode('rescue'); }}>
              {t('save.rescueShow')}
            </button>
            <button class="button-small" onClick={() => { forgetRescuedSave(); setMode('none'); setRescueStuck(false); }}>
              {t('save.rescueForget')}
            </button>
          </div>
          {rescueStuck && <p class="warning">{t('save.rescueStuck')}</p>}
        </div>
      )}
      {mode === 'rescue' && rescue !== null && (
        <label class="field">
          <span>{t('save.rescueShown')}</span>
          <textarea readOnly rows={4} value={rescue} onFocus={(event) => (event.target as HTMLTextAreaElement).select()} />
        </label>
      )}
      {mode === 'import' && (
        <>
          <label class="field">
            <span>{t('save.importLabel')}</span>
            <textarea rows={4} value={text} onInput={(event) => setText((event.target as HTMLTextAreaElement).value)} />
          </label>
          <button class="button-small" onClick={load}>
            {t('save.importConfirm')}
          </button>
          {error && <p class="warning">{t('save.importError')}</p>}
        </>
      )}
    </Card>
  );
}

function DevPanel({ state }: { state: GameState }) {
  return (
    <Card title={t('dev.title')} className="dev">
      <p class="muted">{t('dev.speed')}</p>
      <div class="button-row">
        {DEV_SPEEDS.map((speed) => (
          <button
            key={speed}
            class={`button-small ${state.speed.dev === speed ? 'active' : ''}`}
            onClick={() => update((s) => setDevSpeed(s, speed))}
          >
            ×{speed}
          </button>
        ))}
      </div>
      <div class="button-row">
        <button class="button-small" onClick={() => update(devAddMoney)}>{t('dev.money')}</button>
        <button class="button-small" onClick={() => update(devSpend)}>{t('dev.spend')}</button>
        <button class="button-small" onClick={() => update(devFillEnergy)}>{t('dev.energy')}</button>
        <button class="button-small" onClick={() => update(devTriggerEvent)}>{t('dev.event')}</button>
        <button class="button-small" onClick={() => update(devFirstJob)}>{t('dev.firstJob')}</button>
        <button class="button-small" onClick={() => update(devAdvanceYear)}>{t('dev.year')}</button>
        <button class="button-small" onClick={() => update(devAddReserve)}>{t('dev.reserve')}</button>
      </div>
    </Card>
  );
}
